import { monitoringExceptionHelper } from 'instrumentation/helpers';
import AxiosService from './AxiosService';

class PluginService {
  /*
   * Singleton ES6 pattern
   * https://www.sitepoint.com/javascript-design-patterns-singleton/
   */
  constructor() {
    if (!PluginService.instance) {
      PluginService.instance = this;
    }
    return PluginService.instance;
  }

  endpoint = '/external-integrations';

  async get(options) {
    const { data } = await AxiosService.get(
      `${this.endpoint}/bootstrap`,
      options
    );

    return data;
  }

  async getReleaseNotes(apiName, page = '1') {
    const { data } = await AxiosService.get(
      `${this.endpoint}/release-notes/${apiName}?page=${page}`
    );

    return data;
  }

  async getPluginApps(search, installed = false, page, pageSize) {
    const { data } = await AxiosService.get(
      `${this.endpoint}/plugin-apps?search=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}&installed=${installed}`
    );

    return data;
  }

  async getBusinessPluginApps(options) {
    try {
      const { data } = await AxiosService.get(
        `${this.endpoint}/business-plugin-apps`,
        options
      );

      return data;
    } catch (ex) {
      // This request needs to gracefully handle errors, since it can be called
      // from outside our normal error boundary. The failure state is to show no plugins
      // if they can't be loaded, rather than break the app.
      monitoringExceptionHelper(ex);

      return [];
    }
  }

  async getEvents(id) {
    if (!id) {
      return {
        events: [],
      };
    }

    try {
      const { data } = await AxiosService.get(
        `${this.endpoint}/business-plugin-apps/${id}/events`
      );

      return data;
    } catch (ex) {
      // This is mostly for dev - if the business plugin app gets deleted, we don't want to break the whole page
      if (ex.status !== 404) {
        throw ex;
      }

      return {
        events: [],
      };
    }
  }

  async getAuthorizedUsers(id, serviceId) {
    const { data } = await AxiosService.get(
      `${this.endpoint}/business-plugin-apps/${id}/services/${serviceId}/authorized-users`
    );

    return data;
  }

  async updateBusinessPluginApp(apiName, payload) {
    const { data } = await AxiosService.patch(
      `${this.endpoint}/business-plugin-apps/${apiName}`,
      payload
    );

    return data;
  }

  async createBusinessPluginApp(payload) {
    const { data } = await AxiosService.post(
      `${this.endpoint}/business-plugin-apps`,
      payload
    );

    return data;
  }

  async upgradeBusinessPluginApp(id, version) {
    const { data } = await AxiosService.post(
      `${this.endpoint}/business-plugin-apps/${id}/upgrade`,
      {
        version,
      }
    );

    return data;
  }

  async getPluginApp(api_name) {
    const { data } = await AxiosService.get(
      `${this.endpoint}/plugin-apps/${api_name}`
    );
    return data;
  }

  async revokeOauthAuthorization(id, service_name) {
    const result = await AxiosService.post(
      `${this.endpoint}/business-plugin-apps/${id}/services/${service_name}/logout`
    );
    return result?.data;
  }

  async performPluginImport(s3FileId, businessPluginAppId) {
    const { data } = await AxiosService.post(`/meta-import`, {
      s3_object_id: s3FileId,
      business_plugin_app_id: businessPluginAppId,
    });

    return data;
  }

  async getTemplateAssociations(businessPluginAppId) {
    const { data } = await AxiosService.get(
      `${this.endpoint}/business-plugin-apps/${businessPluginAppId}/browser-js-action-template-associations`
    );

    return data?.associations;
  }

  async createTemplateAssociation(
    templateAPIName,
    pluginAppAPIName,
    customObjectId,
    includePerformAction = false
  ) {
    const { data } = await AxiosService.post(
      `${this.endpoint}/browser-js-action-template-association`,
      {
        custom_object: {
          id: customObjectId,
        },
        browser_js_action_template: {
          api_name: templateAPIName,
          plugin_app_api_name: pluginAppAPIName,
        },
        include_perform_action: includePerformAction,
      }
    );

    return data;
  }

  async deleteTemplateAssociation(associationId) {
    const { data } = await AxiosService.delete(
      `${this.endpoint}/browser-js-action-template-association/${associationId}`
    );

    return data;
  }
}

const instance = new PluginService();
Object.freeze(instance);

export default instance;
